import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

interface SyncConfig {
  max_videos: number;
  max_comments_per_video: number;
  updated_at: string;
  updated_by: string;
}

/**
 * Channel sync limits — YouTube per-user OAuth quotas are the real ceiling,
 * so `0` means "unlimited". Admins set positive numbers to cap what SociBaba
 * pulls on very large channels during validation.
 */
export const SyncLimitsCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";
  const [maxVideos, setMaxVideos] = useState<string>("0");
  const [maxComments, setMaxComments] = useState<string>("0");

  const cfg = useQuery<SyncConfig>({
    queryKey: ["admin-sync-config"],
    queryFn: async () => (await api.get("/admin/sync-config")).data,
  });

  useEffect(() => {
    if (cfg.data) {
      setMaxVideos(String(cfg.data.max_videos ?? 0));
      setMaxComments(String(cfg.data.max_comments_per_video ?? 0));
    }
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: async () =>
      (await api.put("/admin/sync-config", {
        max_videos: Math.max(0, parseInt(maxVideos || "0", 10) || 0),
        max_comments_per_video: Math.max(0, parseInt(maxComments || "0", 10) || 0),
      })).data as SyncConfig,
    onSuccess: () => {
      toast.success("Sync limits updated");
      qc.invalidateQueries({ queryKey: ["admin-sync-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const isUnlimitedVideos = parseInt(maxVideos || "0", 10) === 0;
  const isUnlimitedComments = parseInt(maxComments || "0", 10) === 0;

  return (
    <div className="sg-card p-6 mt-6" data-testid="sync-limits-card">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="sg-label mb-1">Channel Sync Limits</div>
          <div className="font-display text-lg font-semibold tracking-tight">
            How much to pull per sync
          </div>
          <p className="text-sm text-[color:var(--sg-muted)] mt-2 max-w-2xl leading-relaxed">
            SociBaba does not impose an app-level rate limit — YouTube&apos;s own per-user OAuth
            quota is the only cap. Leave both fields at <span className="font-mono">0</span> to pull
            <b> every</b> video and <b>every</b> comment for each user. Set a positive number to
            cap the sync (useful when validating on very large channels).
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider">Max videos per sync</Label>
          <Input
            data-testid="sync-max-videos-input"
            type="number"
            min={0}
            value={maxVideos}
            disabled={readonly}
            onChange={(e) => setMaxVideos(e.target.value)}
            placeholder="0 = unlimited"
          />
          <div className="text-[11px] text-[color:var(--sg-muted)] mt-1">
            {isUnlimitedVideos ? "Unlimited — pulls all uploads" : `Cap: ${maxVideos} videos`}
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Max comments per video</Label>
          <Input
            data-testid="sync-max-comments-input"
            type="number"
            min={0}
            value={maxComments}
            disabled={readonly}
            onChange={(e) => setMaxComments(e.target.value)}
            placeholder="0 = unlimited"
          />
          <div className="text-[11px] text-[color:var(--sg-muted)] mt-1">
            {isUnlimitedComments ? "Unlimited — pulls all comment threads" : `Cap: ${maxComments} comments`}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-[color:var(--sg-muted)]">
          {cfg.data?.updated_at ? (
            <>Last updated {new Date(cfg.data.updated_at).toLocaleString()}</>
          ) : (
            <>Never updated — using defaults (unlimited).</>
          )}
        </div>
        {!readonly && (
          <Button
            data-testid="save-sync-limits-btn"
            disabled={save.isPending}
            onClick={() => save.mutate()}
            className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
          >
            {save.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save sync limits
          </Button>
        )}
      </div>
    </div>
  );
};
